<script setup lang="ts">
/**
 * Singapura — ROND, RONS & RORC (Existing) 통합 (CSI004). Studio panel: 3종 × 인물별 복제 + create.
 *   ROND=이사별 · RONS=주주별 · RORC=지배자별. per-person 스코프(rond_{id}/ · rons_{id}/ · rorc_{id}/).
 *   자동채움: 회사·UEN + 이사/주주/지배자 목록 → 각 스코프 이름·서명자 시드 + 부수 개수(autoLocal).
 */
import { ref, computed, watch, onMounted, provide } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail } from '@/features/AI-documents/types/docGen.types';
import type { SignerEntry } from '@/features/AI-documents/types/docGen.types';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import { provideAgmFill, bareKey, typeOf } from '../../aura/agm/cdd/useCddFill';
import { provideAgmSigners, AGM_ENTITY_CD } from '../../aura/agm/cdd/agmSignersContext';
import RegistersPackageDoc from '../registers/RegistersPackageDoc.vue';
import { normalizeSingapuraRegistersForWord } from '../utils/normalizeSingapuraRegistersForWord';
import { formatAppliedAddress } from '../utils/formatAppliedAddress';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { entityMyApi } from '@/features/Entity_my/api/entityApi';
import { contactsApi } from '@/features/Contact/api/contactsApi';
import { entityApi } from '@/features/Entity/api/entityApi';
import { isIndividualNominator, formatNominatorAddress, formatNominatorPhone } from '@/features/Entity/utils/nominatorFormat';
import { fetchActiveEntityControllers } from '@/features/startTask/preset_task/sg/components/controller/confirmation/composables/entityControllersApi';
import { loadCmBaseCodeOptions, nationalityLabelForOutput } from '@/features/startTask/preset_task/sg/composables/cmBaseCodeOptions';
import { ensureB15JurisdictionNameMap, resolveB15CodeToName } from '@/features/startTask/preset_task/sg/components/controller/notice-corporate/composables/b15JurisdictionDisplay';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  appliedAddress?: { key: string; line?: string; line1?: string; line2?: string; line3?: string; town?: string; state?: string; postcode?: string; country?: string; seq?: number } | null;
  editTaskId?: number | null;
  studio?: boolean;
  chatFormData?: Record<string, any> | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const router = useRouter();
const fill = provideAgmFill();
const signersCtx = provideAgmSigners();
const autofilledKeys = ref<Set<string>>(new Set());
const lastAutofillCd = ref('');
const taskConfigSteps = ref<string[]>([]);
const TASK_CODE = 'CSI004';
let signerSeq = 0;
function mkSigner(name: string, title: string, role = 'director'): SignerEntry {
  return { id: `reg-si-${signerSeq++}`, name: String(name || '').trim(), title, role, isManual: true, showTitle: true, _signerLayout: 'box' } as SignerEntry;
}

// 부수 개수(autoLocal) → 패키지에 반영
const autoLocal = ref<{ rondIds: number[]; ronsIds: number[]; rorcIds: number[] } | null>(null);
const docRef = ref<any>(null);

const entityCd = computed(() =>
  String(props.entityData?.entityCd || props.entityData?.icpCd || props.chatFormData?.entityCd || props.chatFormData?.icpCd || '').trim(),
);
provide(AGM_ENTITY_CD, entityCd);

const taskTitleLocal = ref('');
const titleModel = computed({ get: () => taskTitleLocal.value || props.taskTitle || '', set: (v: string) => { taskTitleLocal.value = v; } });
const effectiveTitle = computed(() => titleModel.value.trim());
const studio = computed(() => !!props.studio);
const editMode = computed(() => !!props.editTaskId);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; presetProgress.message = v ? progress.value || 'Creating task…' : ''; });
watch(progress, (m) => { if (creating.value) presetProgress.message = m; });

function emitProgress() {
  const total = fill.totalFields.value || 1;
  emit('progress-change', Math.min(100, Math.round((fill.filledCount.value / total) * 100)));
}
watch([() => fill.filledCount.value, () => fill.totalFields.value], () => emitProgress());

const st = (v: unknown) => String(v ?? '').trim();
function isoDate(v: unknown): string {
  const s = st(v); const m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
  const d = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/); // 등록부/직원 API(CONVERT 103) dd/mm/yyyy
  return d ? `${d[3]}-${d[2].padStart(2, '0')}-${d[1].padStart(2, '0')}` : s;
}

function buildAddress(c: any): string {
  if (!c || typeof c !== 'object') return '';
  const g = (...ks: string[]) => { for (const k of ks) { const v = st((c as any)[k]); if (v) return v; } return ''; };
  const fl1 = g('foreignAddressLine1', 'FOREIGN_ADDRESS_LINE_1'); const fl2 = g('foreignAddressLine2', 'FOREIGN_ADDRESS_LINE_2');
  if (fl1 || fl2) return [fl1, fl2].filter(Boolean).join(', ');
  const block = g('blockNo', 'BLOCK_NO'); const street = g('streetName', 'STREET_NAME'); const building = g('buildingName', 'BUILDING_NAME');
  const level = g('addrLevel', 'ADDR_LEVEL'); const unitNo = g('addrUnit', 'ADDR_UNIT'); const postal = g('postalCode', 'POSTAL_CODE');
  const unit = [level, unitNo].filter(Boolean).join('-');
  return [[block, street].filter(Boolean).join(' '), building, unit ? `#${unit}` : '', postal ? `Singapore ${postal}` : ''].filter(Boolean).join(', ');
}
/** 컨택트 상세 → 주소/국적/신분증/이메일/연락처/생년 보강값. */
async function enrichContact(contactCd: string): Promise<Record<string, string>> {
  const cd = st(contactCd); if (!cd) return {};
  try {
    const raw: any = await contactsApi.getContactById(cd);
    const c: any = raw?.contact || raw?.data?.contact || raw?.data || raw || {};
    const out: Record<string, string> = {};
    const addr = buildAddress(c); if (addr) out.residential_address = addr;
    const email = st(c.email || c.CONTACT_EMAIL || c.contactEmail); if (email) out.email = email;
    const mobile = [st(c.areaCode || c.AREA_CODE), st(c.mobileNo || c.MOBILE_NO)].filter(Boolean).join(' '); if (mobile) out.mobile = mobile;
    const natCd = st(c.nationalityCd || c.NATIONALITY_CD);
    const natName = st(c.nationalityName || c.NATIONALITY_NAME || c.nationality);
    const nat = natCd ? (nationalityLabelForOutput(natCd) || natName || natCd) : natName;
    if (nat) out.nationality = nat;
    const id = st(c.contactRegNo || c.CONTACT_REG_NO || c.nricNo || c.passportNo); if (id) out.id_no = id;
    const dob = isoDate(c.dateOfBirth || c.DATE_OF_BIRTH || c.dob); if (dob) out.date_of_birth = dob;
    const rep = st(c.representativeName || c.REPRESENTATIVE_NAME); if (rep) out.representative_name = rep; // 법인 컨택트 대표자
    return out;
  } catch { return {}; }
}

// person lookup — 스코프 인물(이사/주주/지배자). RONS 주주 칸은 주주 목록에서 고른다.
function lookupRoleFor(bare: string): string {
  if (bare === 'shareholder_name') return 'SHAREHOLDER';
  if (bare === 'controller_rep_name') return 'CONTACT'; // 법인 지배자(RORC Corp)의 서명 대표자 — 회사 밖 컨택트
  if (bare === 'controller_full_name' || bare === 'controller_name') return 'CONTROLLER'; // RORC — 회사 컨트롤러 등록부 목록
  return 'DIRECTOR';
}
fill.setActivateHandler((key: string) => {
  const t = typeOf(key);
  if (t === 'entity') { emit('change-entity'); return; }
  if (t === 'person') {
    emit('request-person-lookup', { fieldGroup: 'singapura-registers', key: bareKey(key), scopedKey: key, defaultRole: lookupRoleFor(bareKey(key)), lookupType: 'officer', entityCd: entityCd.value });
  }
});
fill.setAddressHandler((key: string) => { emit('request-address-input', key); });
watch(() => props.selectedPerson, async (p) => {
  const ak = fill.activeKey.value;
  if (!p || !ak) return;
  // RORC 이름 칸에서 컨트롤러를 고르면 그 부수 전체(개인/법인 구분·항목·서명자)를 그 컨트롤러로 채운다
  const m = ak.match(/^(rorc_\d+\/)(.+)$/);
  if (m && RORC_PICK_KEYS.has(m[2])) { await applyControllerPick(m[1], p as Record<string, any>); emitProgress(); return; }
  fill.applyPersonToActive(p as Record<string, any>); emitProgress();
});
// 📍 주소 입력 → Enter Address 결과를 문서 칸에 반영 (SG 입력은 "…, Singapore 우편번호" 까지).
watch(() => props.selectedAddress, (a) => {
  if (!a?.value || !fill.activeKey.value) return;
  fill.setValue(fill.activeKey.value, a.value); emitProgress();
});
watch(() => props.appliedAddress, (a) => {
  if (!a?.key) return;
  const line = formatAppliedAddress(a);
  if (line) { fill.setValue(a.key, line); emitProgress(); }
}, { deep: true });

// ── RORC 컨트롤러 1부 채우기 — 회사 자동채움과 ✨ 컨트롤러 선택이 같이 쓴다 ──
type CtrlSeed = { name: string; contactCd: string; idNo: string; becameDate: string; member: any | null; corp: boolean; reg: any | null };
/** 마지막 회사 자동채움 기준(주주 목록·총 주식수·비서·관할 코드표·컨트롤러 등록부) */
let ctrlCtx: { named: any[]; totalShares: number; secName: string; b15: Map<string, string>; registered: any[] } = { named: [], totalShares: 0, secName: '', b15: new Map(), registered: [] };
/** 컨트롤러 상세(L_CONTROLLERMANAGE) — 등록사무소/거주 주소·법적형태·대표자·연락처는 컨택트가 아니라 여기에 있다 */
async function fetchControllerDetail(reg: any, corp: boolean): Promise<Record<string, any>> {
  const ctrlIdx = st(reg?.ctrlIdx);
  if (!ctrlIdx || !entityCd.value) return {};
  try {
    const rt = st(reg?.registerType) || (corp ? 'A06002' : 'A06001');
    const res: any = await fetch(`/api/entity/controller/${encodeURIComponent(entityCd.value)}/detail?ctrlIdx=${encodeURIComponent(ctrlIdx)}&registerType=${encodeURIComponent(rt)}`, { credentials: 'include' }).then((r) => r.json());
    return res?.data ?? res ?? {};
  } catch (e) { console.warn('[Registers] controller detail failed', e); return {}; }
}
async function fillControllerScope(sc: string, c: CtrlSeed, set: (k: string, v: unknown) => void, setSig: (k: string, list: SignerEntry[]) => void) {
  const { totalShares, secName, b15 } = ctrlCtx;
  const nm = c.name; const m = c.member;
  set(`${sc}controller_name`, nm); set(`${sc}controller_full_name`, nm);
  set(`${sc}controller_id_no`, c.idNo);
  if (c.becameDate) set(`${sc}controller_became_date`, c.becameDate);
  const sh = Number(m?.sharesTotal) || 0;
  if (m && totalShares > 0 && sh > 0) set(`${sc}controller_shareholding`, String(Math.round((sh / totalShares) * 10000) / 100));
  if (m) set(`${sc}controller_designation`, m?.isIndividual === false ? 'Corporate Shareholder' : 'Shareholder');
  // 회신 주소 = 원본 Word(AR-E-RORC) 의 고정 주소(회사 비서 사무실). 회사 등록사무소가 아님 — 화면에서 수정 가능.
  set(`${sc}reply_address`, RORC_REPLY_ADDRESS);
  if (secName) { set(`${sc}secretary_name`, secName); set(`${sc}secretary_designation`, 'Company Secretary'); }
  const ecd = st(c.contactCd || m?.contactCd);
  const enr = ecd ? await enrichContact(ecd) : {};
  const dt = c.reg ? await fetchControllerDetail(c.reg, c.corp) : {}; // 컨트롤러 상세 우선, 없으면 컨택트 값
  const dtAddr = buildAddress(dt);
  const dtPhone = [st(dt.areaCode), st(dt.mobileNo)].filter(Boolean).join(' ');
  // 법인 지배자 → AR-E-RORC (Corp) 양식(controller_type) + 법인 항목(UEN·등록사무소·법적형태·관할/법령·등록부) · 서명 = 대표자
  set(`${sc}controller_type`, c.corp ? 'corporate' : 'individual');
  if (c.corp) {
    const r = c.reg || {};
    const uen = st(dt.acrAuen || dt.nonAcrAuen || r.regNo || r.contactRegNo || c.idNo || m?.registrationNo);
    set(`${sc}controller_uen`, uen);
    const office = dtAddr || enr.residential_address;
    if (office) set(`${sc}controller_registered_office`, office);
    set(`${sc}controller_legal_form`, dt.legalForm || r.legalForm);
    const email = st(dt.contactEmail) || enr.email; if (email) set(`${sc}controller_email`, email);
    const phone = dtPhone || enr.mobile; if (phone) set(`${sc}controller_phone`, phone);
    const jur = st(dt.jurisdictionName) || resolveB15CodeToName(st(dt.jurisdiction || r.jurisdictionCd), b15);
    set(`${sc}controller_jurisdiction_statute`, [jur, st(dt.incorporationStatute || r.incorporationStatute)].filter(Boolean).join(', '));
    set(`${sc}controller_register_name`, dt.corporateEntityRegister || r.corporateEntityRegister);
    set(`${sc}controller_register_id`, st(r.nonRegNo) || uen);
    if (dt.dateOfAppointed && !c.becameDate) set(`${sc}controller_became_date`, isoDate(dt.dateOfAppointed));
    const rep = st(dt.representativeName) || st(enr.representative_name);
    if (rep) set(`${sc}controller_rep_name`, rep);
    set(`${sc}controller_designation`, `Authorised representative of ${nm}`);
    // 서명 = 법인의 대표자(서명란 아래 이름). 대표자가 아직 없으면 법인명 — Name(대표자) 칸을 채우면 서명자도 따라 바뀐다.
    setSig(`${sc}rorc_Signers`, [mkSigner(rep || nm, 'Authorised Representative', 'contact')]);
    return;
  }
  const resAddr = dtAddr || enr.residential_address;
  if (resAddr) set(`${sc}controller_residential_address`, resAddr);
  const nat = st(dt.nationalityName) || enr.nationality; if (nat) set(`${sc}controller_nationality`, nat);
  const idNo = st(dt.identificationNo) || enr.id_no; if (idNo) set(`${sc}controller_id_no`, idNo);
  const email = st(dt.contactEmail) || enr.email; if (email) set(`${sc}controller_email`, email);
  const mobile = dtPhone || enr.mobile; if (mobile) set(`${sc}controller_mobile`, mobile);
  const dob = isoDate(dt.dateOfBirth) || enr.date_of_birth; if (dob) set(`${sc}controller_date_of_birth`, dob);
  if (st(dt.aliases)) set(`${sc}controller_aliases`, dt.aliases);
  if (dt.dateOfAppointed && !c.becameDate) set(`${sc}controller_became_date`, isoDate(dt.dateOfAppointed));
  setSig(`${sc}rorc_Signers`, [mkSigner(nm, 'Controller', 'contact')]);
}

/** ✨ RORC 이름 칸에서 컨트롤러 목록(CONTROLLER)으로 고른 경우 — 그 부수 전체를 그 컨트롤러로 다시 채운다 */
const RORC_PICK_KEYS = new Set(['controller_full_name', 'controller_name']);
// 컨트롤러가 바뀌면 이전 사람 값이 남지 않게 지우는 칸(회신주소·비서·날짜·Yes/No 등 서식 공통 값은 유지)
const RORC_PERSON_KEYS = ['controller_name', 'controller_full_name', 'controller_type', 'controller_id_no', 'controller_became_date',
  'controller_shareholding', 'controller_designation', 'controller_aliases', 'controller_residential_address', 'controller_nationality',
  'controller_date_of_birth', 'controller_email', 'controller_mobile', 'controller_uen', 'controller_registered_office', 'controller_legal_form',
  'controller_phone', 'controller_jurisdiction_statute', 'controller_register_name', 'controller_register_id', 'controller_rep_name'];
async function applyControllerPick(sc: string, p: Record<string, any>) {
  const name = st(p.name || p.contactName || p.fullName);
  if (!name) return;
  const contactCd = st(p.contactCd || p.referencesContactCd);
  // 선택값(selectedPerson)에는 개인/법인 구분·법인 항목이 빠져 올 수 있다 → 컨트롤러 등록부 행으로 다시 찾는다
  if (!ctrlCtx.registered.length && entityCd.value) ctrlCtx.registered = await fetchActiveEntityControllers(entityCd.value).catch(() => []);
  const ctrlIdx = st(p.ctrlIdx || p.id);
  const reg = ctrlCtx.registered.find((r: any) => (ctrlIdx && st(r.ctrlIdx) === ctrlIdx)
    || (contactCd && st(r.referencesContactCd) === contactCd)
    || st(r.fullName).toUpperCase() === name.toUpperCase()) || null;
  const member = ctrlCtx.named.find((m: any) => (contactCd && st(m?.contactCd) === contactCd) || st(m?.name).toUpperCase() === name.toUpperCase()) || null;
  const regNo = st(reg?.contactRegNo || reg?.regNo || p.contactRegNo || p.regNo);
  const seed: CtrlSeed = {
    name: st(reg?.fullName) || name, contactCd: st(reg?.referencesContactCd) || contactCd, idNo: regNo,
    becameDate: isoDate(reg?.becomingDate || p.becomingDate || p.appointmentDate), member,
    corp: st(reg?.categoryCd || p.categoryCd) === 'B09001',
    reg: reg || { regNo, contactRegNo: regNo, legalForm: p.legalForm, jurisdictionCd: p.jurisdiction, incorporationStatute: p.incorporationStatute, corporateEntityRegister: p.corporateEntityRegister, nonRegNo: '' },
  };
  for (const k of RORC_PERSON_KEYS) fill.setValue(`${sc}${k}`, '');
  await fillControllerScope(sc, seed, (k, v) => { const s = st(v); if (s) fill.setValue(k, s); }, (k, list) => signersCtx.setSigners(k, list));
}

/**
 * RORC (Corp) 서명자 = 대표자 — Name(controller_rep_name) 칸이 바뀌면 그 부수 서명자를 대표자로 맞춘다.
 *   서명자를 사람이 직접 바꾼 경우(서명자 1명이 아니거나, 이름이 법인명·이전 대표자가 아님)는 건드리지 않는다.
 */
const lastRepByScope: Record<string, string> = {};
watch(() => Object.entries(fill.values).filter(([k]) => /^rorc_\d+\/controller_rep_name$/.test(k)).map(([k, v]) => `${k}=${st(v)}`).join('|'), () => {
  for (const [k, v] of Object.entries(fill.values)) {
    const m = k.match(/^(rorc_\d+\/)controller_rep_name$/);
    if (!m) continue;
    const sc = m[1]; const rep = st(v); const prev = lastRepByScope[sc] ?? '';
    lastRepByScope[sc] = rep;
    if (!rep || rep === prev || st(fill.values[`${sc}controller_type`]) !== 'corporate') continue;
    const sk = `${sc}rorc_Signers`;
    const cur = signersCtx.signersOf(sk);
    const corpName = st(fill.values[`${sc}controller_name`]).toUpperCase();
    const auto = cur.length === 0 || (cur.length === 1 && [corpName, prev.toUpperCase()].includes(st(cur[0].name).toUpperCase()));
    if (auto) signersCtx.setSigners(sk, [mkSigner(rep, 'Authorised Representative', 'contact')]);
  }
});

// 자동채움 — 회사 + 이사/주주/지배자 목록 → 스코프 이름·서명자 + 부수 개수
//   Edit: 저장값 복원(restoreState)이 진행 중인 자동채움을 취소한다(autofillRun 토큰) — 안 그러면 자동채움이
//   복원보다 늦게 끝나 직접 고친 값·서명자·부수를 회사 데이터로 덮어쓴다. Edit 중 회사를 바꾸면 그때는 자동채움.
let autofillRun = 0;
async function autofillFromEntity(cdRaw: string) {
  const cd = st(cdRaw);
  if (!cd || cd === lastAutofillCd.value) return;
  lastAutofillCd.value = cd;
  const run = ++autofillRun;
  const alive = () => run === autofillRun;
  await loadCmBaseCodeOptions('B04').catch(() => { /* 국적 라벨 매핑 — 실패해도 진행 */ });
  if (!alive()) return;
  for (const k of autofilledKeys.value) fill.setValue(k, '');
  signersCtx.clearAll();
  const written = new Set<string>();
  const set = (k: string, v: unknown) => { if (!alive()) return; const s = st(v); if (s) { fill.setValue(k, s); written.add(k); } };
  const setSig = (k: string, list: SignerEntry[]) => { if (alive()) signersCtx.setSigners(k, list); };

  let members: any[] = [];
  let regOffice = '';
  try {
    const res: any = await fetch(`/api/entity/${encodeURIComponent(cd)}/agm-source`, { credentials: 'include' }).then((r) => r.json());
    const src: any = res?.data?.data ?? res?.data ?? res ?? {};
    set('company_name', src.companyName);
    set('company_uen', src.registrationNo);
    regOffice = st(src.registeredOfficeAddress);
    members = Array.isArray(src.members) ? src.members : [];
  } catch (e) { console.warn('[Singapura-Registers] agm-source failed', e); }

  const officersOf = async (group: 'director' | 'secretary'): Promise<any[]> => {
    try {
      const res: any = await entityMyApi.getEntityOfficer(cd, { positionGroup: group });
      const off: any = res?.data?.data ?? res?.data ?? res ?? {};
      return [...(off.autoOfficers || []), ...(off.manualOfficers || [])].filter((o) => st(o?.cessationDate) === '' && st(o?.status) !== 'Cessation');
    } catch { return []; }
  };

  // 지명자(nominator) — L_NOMINATOR(싱크·엔티티 입력, bizfile 원본 필드) 우선, 예전 데이터(컨택트 코드)는 Global Contact 로 채움.
  //   /entity/:id/nominator 가 둘 다 같은 bizfile 형식(+ 국적·국가 표시명)으로 돌려준다.
  async function enrichNominator(nominatorCd: string, sc: string, type: 'director' | 'shareholder') {
    const ncd = st(nominatorCd); if (!ncd) return;
    try {
      const res: any = await entityApi.getEntityNominator(cd, ncd, type);
      const payload: any = res?.data ?? null;
      const data: any = payload?.nominator ? payload : (payload?.data ?? null);
      const n: any = data?.nominator;
      if (n && typeof n === 'object') {
        const disp: any = data?.display || {};
        const email = st(n.EmailAddress);
        const phone = formatNominatorPhone(n);
        const addr = formatNominatorAddress(n);
        const became = isoDate(n.NominationDate);
        if (isIndividualNominator(n)) {
          set(`${sc}ind_nominator_full_name`, n.Name || n.EntityName);
          set(`${sc}ind_nominator_aliases`, n.NameAlias);
          set(`${sc}ind_nominator_residential_address`, addr);
          set(`${sc}ind_nominator_nationality`, disp.Nationality || n.Nationality);
          set(`${sc}ind_nominator_id_no`, n.IdentificationNo);
          set(`${sc}ind_nominator_date_of_birth`, isoDate(n.BirthDate));
          set(`${sc}ind_nominator_email`, email);
          set(`${sc}ind_nominator_mobile`, phone);
          set(`${sc}ind_nominator_became_date`, became);
        } else {
          set(`${sc}ent_nominator_name`, n.EntityName || n.Name);
          set(`${sc}ent_nominator_uen`, n.Uen);
          set(`${sc}ent_nominator_registered_office_address`, addr);
          set(`${sc}ent_nominator_legal_form`, n.CorporateControllerLegalForm);
          set(`${sc}ent_nominator_email`, email);
          set(`${sc}ent_nominator_phone`, phone);
          set(`${sc}ent_nominator_jurisdiction`, [st(disp.CountryOfRegistration || n.CountryOfRegistration), st(n.Statute)].filter(Boolean).join(', '));
          set(`${sc}ent_nominator_register_name`, n.CorporateEntityRegisterName);
          set(`${sc}ent_nominator_register_id`, n.Uen);
          set(`${sc}ent_nominator_became_date`, became);
        }
        return;
      }
    } catch (e) { console.warn('[Registers] nominator detail failed — contact fallback', e); }
    try {
      const raw: any = await contactsApi.getContactById(ncd);
      const c: any = raw?.contact || raw?.data?.contact || raw?.data || raw || {};
      const isEntity = st(c.categoryCd || c.CATEGORY_CD) === 'B09001'; // B09001=법인, B09002=개인
      const name = st(c.contactName || c.CONTACT_NAME);
      const addr = buildAddress(c);
      const email = st(c.email || c.CONTACT_EMAIL || c.contactEmail);
      const mobile = [st(c.areaCode || c.AREA_CODE), st(c.mobileNo || c.MOBILE_NO)].filter(Boolean).join(' ');
      const regNo = st(c.contactRegNo || c.CONTACT_REG_NO);
      if (isEntity) {
        set(`${sc}ent_nominator_name`, name);
        set(`${sc}ent_nominator_uen`, regNo);
        set(`${sc}ent_nominator_registered_office_address`, addr);
        set(`${sc}ent_nominator_legal_form`, c.legalForm || c.LEGAL_FORM);
        set(`${sc}ent_nominator_email`, email);
        set(`${sc}ent_nominator_phone`, mobile);
        set(`${sc}ent_nominator_jurisdiction`, c.incorporationStatute || c.INCORPORATION_STATUTE || c.jurisdiction || c.JURISDICTION);
        set(`${sc}ent_nominator_register_name`, c.corporateEntityRegister || c.CORPORATE_ENTITY_REGISTER);
        set(`${sc}ent_nominator_register_id`, regNo);
      } else {
        const natCd = st(c.nationalityCd || c.NATIONALITY_CD);
        const natName = st(c.nationalityName || c.NATIONALITY_NAME || c.nationality);
        set(`${sc}ind_nominator_full_name`, name);
        set(`${sc}ind_nominator_residential_address`, addr);
        set(`${sc}ind_nominator_nationality`, natCd ? (nationalityLabelForOutput(natCd) || natName || natCd) : natName);
        set(`${sc}ind_nominator_id_no`, regNo);
        set(`${sc}ind_nominator_date_of_birth`, isoDate(c.dateOfBirth || c.DATE_OF_BIRTH));
        set(`${sc}ind_nominator_email`, email);
        set(`${sc}ind_nominator_mobile`, mobile);
      }
    } catch (e) { console.warn('[Registers] enrichNominator failed', e); }
  }

  /** 같은 사람(컨택트/이름)이 여러 행(예: Director + Managing Director)이면 1부만. */
  const personKey = (cd: unknown, nm: unknown) => (st(cd) || st(nm)).toUpperCase();

  // ROND — **재직 이사 전원** 1부씩(각자 지명 여부를 회신하는 서식). 지명(nominee) 이사면 지명자 표까지 채움.
  //   (예전엔 nominee 이사만 만들어 지명 이사가 없는 회사는 0부였다 — 고객 민원)
  const dirOfficers = await officersOf('director');
  const rondIds: number[] = [];
  const seenDir = new Set<string>();
  let di = 0;
  for (const o of dirOfficers) {
    const nm = st(o.fullName || o.contactName); if (!nm) continue;
    const pk = personKey(o.contactCode || o.contactCd, nm); if (seenDir.has(pk)) continue; seenDir.add(pk);
    di += 1; const id = di; rondIds.push(id); const sc = `rond_${id}/`;
    set(`${sc}director_name`, nm); setSig(`${sc}rond_Signers`, [mkSigner(nm, 'Director', 'director')]);
    if (o?.isNominee === true) await enrichNominator(st(o.nomineeDirectorCode || o.nominatorContactCd || o.NOMINATOR_CONTACT_CD), sc, 'director');
  }

  // RONS — **주주 전원** 1부씩. 지명(nominee) 주주면 지명자 표까지 채움.
  const named = members.filter((m: any) => st(m?.name));
  const ronsIds: number[] = [];
  const seenSh = new Set<string>();
  let si = 0;
  for (const m of named) {
    const nm = st(m?.name);
    const pk = personKey(m?.contactCd, nm); if (seenSh.has(pk)) continue; seenSh.add(pk);
    si += 1; const id = si; ronsIds.push(id); const sc = `rons_${id}/`;
    set(`${sc}shareholder_name`, nm); setSig(`${sc}rons_Signers`, [mkSigner(nm, 'Shareholder', 'shareholder')]);
    if (m?.isNominee === true) await enrichNominator(st(m?.nominatorContactCd), sc, 'shareholder');
  }

  // 회사 비서(RORC 발신자) — 첫 재직 비서.
  const secOfficers = await officersOf('secretary');
  const sec0 = secOfficers[0];
  const secName = sec0 ? st(sec0.fullName || sec0.contactName) : '';

  // RORC — **엔티티 컨트롤러 등록부(L_CONTROLLERMANAGE)의 재직 컨트롤러 전원** 1부씩.
  //   등록된 컨트롤러가 없을 때만 예전처럼 지분 25% 초과 주주로 대체(운영자가 가감).
  //   컨트롤러 컨택트 상세(주소·국적·신분증·이메일·연락처·생년) + 회신주소·비서·지분·지배자 편입일까지 자동채움.
  const totalShares = named.reduce((s: number, m: any) => s + (Number(m?.sharesTotal) || 0), 0);
  const pctOf = (m: any) => (totalShares > 0 ? ((Number(m?.sharesTotal) || 0) / totalShares) * 100 : 0);
  const b15 = await ensureB15JurisdictionNameMap().catch(() => new Map<string, string>()); // 법인 지배자 관할(B15) 코드 → 이름
  const registered = await fetchActiveEntityControllers(cd).catch(() => []);
  ctrlCtx = { named, totalShares, secName, b15, registered }; // ✨ 컨트롤러 선택(applyControllerPick)에서도 같은 기준으로 채운다
  const seenCtrl = new Set<string>();
  const ctrlSeeds: CtrlSeed[] = [];
  for (const r of registered) {
    const pk = personKey(r.referencesContactCd, r.fullName); if (seenCtrl.has(pk)) continue; seenCtrl.add(pk);
    const member = named.find((m: any) => (st(r.referencesContactCd) && st(m?.contactCd) === st(r.referencesContactCd))
      || st(m?.name).toUpperCase() === st(r.fullName).toUpperCase()) || null;
    ctrlSeeds.push({ name: r.fullName, contactCd: r.referencesContactCd, idNo: st(r.contactRegNo || r.regNo || r.nonRegNo), becameDate: isoDate(r.becomingDate), member,
      corp: st(r.categoryCd) === 'B09001', reg: r });
  }
  if (!ctrlSeeds.length) {
    for (const m of named.filter((x: any) => pctOf(x) > 25)) ctrlSeeds.push({ name: st(m?.name), contactCd: st(m?.contactCd), idNo: st(m?.registrationNo), becameDate: '', member: m, corp: m?.isIndividual === false, reg: null });
  }
  const rorcIds: number[] = [];
  let ci = 0;
  for (const c of ctrlSeeds) {
    ci += 1; const id = ci; rorcIds.push(id);
    await fillControllerScope(`rorc_${id}/`, c, set, setSig);
  }

  if (!alive()) return;
  autoLocal.value = { rondIds, ronsIds, rorcIds };
  docRef.value?.restoreLocalState?.({ rondIds, ronsIds, rorcIds });
  autofilledKeys.value = written;
  emitProgress();
}
watch(() => props.chatFormData, (fd) => {
  if (!fd) return;
  const nm = st(fd.company_name || fd.entityName);
  if (nm && fill.values.company_name !== nm) fill.setValue('company_name', nm);
  const cd = st(fd.entityCd || fd.icpCd);
  if (cd) void autofillFromEntity(cd);
}, { deep: true });
watch(entityCd, (cd) => { if (cd) void autofillFromEntity(cd); }, { immediate: true });

onMounted(async () => {
  emitProgress();
  try { const cfg: any = await getTaskConfigByCode(st(props.selection.task.TASK_CODE) || TASK_CODE); taskConfigSteps.value = Array.isArray(cfg?.processSteps) ? cfg.processSteps : []; } catch { /* ignore */ }
});

/** Notice of Controllers(RORC) 회신 주소 — 고객 원본 Word 템플릿의 고정 문구 */
const RORC_REPLY_ADDRESS = '60 Paya Lebar Road #05-09, Paya Lebar Square, Singapore 409051';

// per-person 스코프에서 top-level 로 상속하면 안 되는 키(회사키 제외 전부)
const REG_OWNED = [
  'director_name', 'shareholder_name', 'controller_name', 'controller_full_name',
  'nominee_dir_status', 'nominee_sh_status', 'nominator_update', 'last_declaration_date', 'last_declaration_date_still',
  'cessation_date', 'nominator_cessation_date', 'declaration_date', 'change_date', 'notice_date',
  'is_still_controller', 'has_relevant_change',
  'reply_address', 'secretary_name', 'secretary_designation', 'controller_designation', 'controller_shareholding',
  'controller_aliases', 'controller_residential_address', 'controller_nationality', 'controller_id_no',
  'controller_date_of_birth', 'controller_email', 'controller_mobile', 'controller_became_date',
  'new_full_name', 'new_aliases', 'new_residential_address', 'new_nationality', 'new_id_no', 'new_email', 'new_mobile',
  // RORC (Corp) — 법인 지배자 양식
  'controller_type', 'controller_uen', 'controller_registered_office', 'controller_legal_form', 'controller_phone',
  'controller_jurisdiction_statute', 'controller_register_name', 'controller_register_id', 'controller_rep_name',
  'new_uen', 'new_registered_office', 'new_legal_form', 'new_phone', 'new_jurisdiction_statute', 'new_register_name',
  'new_register_id', 'new_became_date',
  'ind_nominator_full_name', 'ind_nominator_aliases', 'ind_nominator_residential_address', 'ind_nominator_nationality',
  'ind_nominator_id_no', 'ind_nominator_date_of_birth', 'ind_nominator_email', 'ind_nominator_mobile',
  'ind_nominator_became_date', 'ind_nominator_ceased_date',
  'ent_nominator_name', 'ent_nominator_uen', 'ent_nominator_registered_office_address', 'ent_nominator_legal_form',
  'ent_nominator_email', 'ent_nominator_phone', 'ent_nominator_jurisdiction', 'ent_nominator_register_name',
  'ent_nominator_register_id', 'ent_nominator_became_date', 'ent_nominator_ceased_date', 'year_ref',
];

function scopedFormData(scopePrefix: string): Record<string, any> {
  const base = fill.exportPayload();
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(base)) {
    if (k.includes('/')) continue;
    if (REG_OWNED.includes(bareKey(k))) continue;
    out[k] = v;
  }
  for (const [k, v] of Object.entries(base)) {
    if (!k.startsWith(scopePrefix)) continue;
    const bk = bareKey(k);
    const sv = st(v);
    if (sv === '' && bk in out && st(out[bk]) !== '') continue;
    out[bk] = v;
  }
  return normalizeSingapuraRegistersForWord(out);
}

function gatherState() { return { values: { ...fill.exportPayload() }, entityCd: entityCd.value, taskTitle: effectiveTitle.value, signers: signersCtx.exportAll(), local: docRef.value?.gatherLocalState?.() }; }
function restoreState(s: any) {
  // 저장된 태스크 — 진행 중인 회사 자동채움을 취소하고, 같은 회사로는 다시 자동채움하지 않는다
  autofillRun += 1;
  const savedCd = st(s?.entityCd) || entityCd.value;
  if (savedCd) lastAutofillCd.value = savedCd;
  if (s?.values) for (const [k, v] of Object.entries(s.values)) if (v != null) fill.setValue(k, String(v));
  if (s?.signers) signersCtx.importAll(s.signers);
  if (s?.local) docRef.value?.restoreLocalState?.(s.local);
}

function classifyDoc(name: string): 'rond' | 'rons' | 'rorc' | 'rorc_corp' | 'other' {
  const s = name.toLowerCase().replace(/[_\-.]+/g, ' ');
  if (/\brond\b/.test(s)) return 'rond';
  if (/\brons\b/.test(s)) return 'rons';
  if (/\brorc\b/.test(s)) return /\bcorp/.test(s) ? 'rorc_corp' : 'rorc'; // "AR-E-RORC (Corp)" = 법인 지배자 양식
  return 'other';
}

async function createTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) { progress.value = 'Please enter a Task Title.'; return; }
  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const taskTypeCd = st(props.selection.task.TASK_CODE) || TASK_CODE;
    const officerData = { entityCd: entityCd.value || null, icpCd: entityCd.value || null, presetForm: gatherState(), docType: 'SG_AR_REGISTERS', isMyPreset: props.selection?.isMyPreset === true, formData: scopedFormData('___company___') };
    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id'; creating.value = false; return; }
      await fetch(`/api/startTask/tasks/${taskId}/data`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ taskData: { formData: officerData } }) });
      await fetch(`/api/startTask/tasks/${taskId}/title`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ taskTitle: effectiveTitle.value }) });
    } else {
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value || null, taskTypeCd,
          taskTypeName: props.selection.task.TASK_NAME || 'ROND, RONS & RORC (Existing)',
          taskTitle: effectiveTitle.value,
          taskFlow: (() => { const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase()); return { createDocument: steps.length === 0 ? true : steps.includes('doc'), requestTSign: steps.includes('t-sign'), proceedWithEFiling: steps.includes('e-filing') }; })(),
          officerData,
        }),
      }).then((r) => r.json());
      taskId = Number(taskRes?.data?.taskId || taskRes?.taskId || 0);
      if (!taskId) { progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`; creating.value = false; return; }
    }

    progress.value = 'Preparing documents…';
    const rawDocs = props.selection.documents || [];
    const seenTmpl = new Set<number>();
    const docs = rawDocs.filter((d: any) => { const tid = Number(d?.tmpId || d?.templateId || 0); if (!tid) return true; if (seenTmpl.has(tid)) return false; seenTmpl.add(tid); return true; });
    const strip = (s: string) => s.split(' — ')[0].trim();
    const local = docRef.value?.gatherLocalState?.() || {};
    type DocSave = { templateId: number; label: string; personName: string; formData: Record<string, any>; signers?: SignerEntry[] };
    const docsToSave: DocSave[] = [];
    const pushOne = (d: any, scope: string, sigKey: string, personKey: string, roleTitle: string, suffix: string) => {
      const tid = Number(d?.tmpId || d?.templateId || 0); if (!tid) return;
      const fd = scopedFormData(scope);
      // Yes/No 토글(회신)은 스코프/최상위 어디에 저장됐든 직접 읽어 강제 반영(REG_OWNED 격리로 누락되는 것 방지).
      for (const yk of ['is_still_controller', 'has_relevant_change']) {
        const sv = st(fill.values[scope + yk]) || st(fill.values[yk]);
        if (sv) fd[yk] = sv;
      }
      const person = st(fd[personKey]) || suffix; // 인물 이름(스코프값), 없으면 'Controller 1' 등 폴백
      const s = signersCtx.signersOf(sigKey);
      // 법인 지배자(RORC Corp) 기본 서명자 = 대표자(없으면 법인명)
      const defaultSigner = st(fd.controller_type) === 'corporate' ? (st(fd.controller_rep_name) || person) : person;
      const signers = s.length ? s : (defaultSigner ? [mkSigner(defaultSigner, roleTitle)] : undefined);
      const baseLabel = strip(String(d?.templateName || d?.docCode || 'Register').replace(/\.docx?$/i, ''));
      // 문서 라벨 접미어 = **인물 이름**(예: "AR-E-RORC — SON JEONG MIN"). 이름 없을 때만 'Controller 1' 폴백.
      docsToSave.push({ templateId: tid, label: `${baseLabel} — ${person}`, personName: person, formData: fd, signers: signers && signers.length ? signers : undefined });
    };
    // RORC 양식 선택 — 부수의 controller_type 이 corporate 면 Corp 양식, 아니면 개인 양식.
    //   프리셋에 Corp 양식이 아직 없으면(템플릿 미등록) 예전처럼 전부 개인 양식으로 만든다.
    const hasCorpTemplate = docs.some((d: any) => classifyDoc(String(d?.templateName || d?.docCode || '')) === 'rorc_corp');
    const isCorpCopy = (id: number) => st(fill.values[`rorc_${id}/controller_type`]) === 'corporate';
    for (const d of docs) {
      const kind = classifyDoc(String((d as any).templateName || (d as any).docCode || ''));
      if (kind === 'rorc_corp') { for (const id of (local.rorcIds || [])) if (isCorpCopy(id)) pushOne(d, `rorc_${id}/`, `rorc_${id}/rorc_Signers`, 'controller_name', 'Authorised Representative', `Controller ${id}`); continue; }
      // nominee/controller 로 시드/추가된 부수만 생성(빈 목록이면 0부 — 전원 생성 아님).
      if (kind === 'rond') { for (const id of (local.rondIds || [])) pushOne(d, `rond_${id}/`, `rond_${id}/rond_Signers`, 'director_name', 'Director', `Director ${id}`); continue; }
      if (kind === 'rons') { for (const id of (local.ronsIds || [])) pushOne(d, `rons_${id}/`, `rons_${id}/rons_Signers`, 'shareholder_name', 'Shareholder', `Shareholder ${id}`); continue; }
      if (kind === 'rorc') { for (const id of (local.rorcIds || [])) if (!hasCorpTemplate || !isCorpCopy(id)) pushOne(d, `rorc_${id}/`, `rorc_${id}/rorc_Signers`, 'controller_name', 'Controller', `Controller ${id}`); continue; }
      // other: 회사 단위 1부
      const od: any = d;
      const tid = Number(od?.tmpId || od?.templateId || 0);
      if (tid) docsToSave.push({ templateId: tid, label: strip(String(od?.templateName || 'Document').replace(/\.docx?$/i, '')), personName: st(fill.values.company_name) || 'Company', formData: scopedFormData('___company___') });
    }

    if (docsToSave.length) {
      try { const r = await fetch('/api/aiDocuments/incorporation/prepare-documents', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }) }).then((x) => x.json()); if (!r?.success) progress.value = `⚠ Documents: ${r?.message || 'unknown'}`; } catch (e: any) { progress.value = `⚠ Document save failed: ${e?.message || e}`; }
    }
    try { await fetch(`/api/startTask/tasks/${taskId}/document-status`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' } }); } catch { /* ignore */ }
    try { await fetch('/api/startTask/preset/use-log', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }) }); } catch { /* ignore */ }
    emit('task-created', { taskId: taskId!, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) { progress.value = e?.message || 'Create failed'; } finally { creating.value = false; }
}

defineExpose({ gatherState, restoreState, createTask });
</script>

<template>
  <div class="sv-panel" :class="{ studio }">
    <div v-if="!studio" class="co-head">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME || 'ROND, RONS & RORC (Existing)' }}</div>
        <div class="co-subtitle">Singapura — Nominee Directors / Shareholders & Controllers registers</div>
      </div>
      <div class="co-actions">
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-task-title')">Change title</v-btn>
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-entity')">Change company</v-btn>
      </div>
    </div>
    <div v-if="!studio" class="co-title-row"><v-text-field v-model="titleModel" density="compact" variant="outlined" hide-details label="Task Title" /></div>
    <div class="co-doc"><RegistersPackageDoc ref="docRef" :auto-local="autoLocal" /></div>
    <div v-if="!studio" class="co-foot">
      <v-btn color="primary" class="text-none" :loading="creating" :disabled="!effectiveTitle || creating" @click="createTask">{{ editMode ? 'Update task' : 'Create Task' }}</v-btn>
      <span v-if="progress && !creating" class="co-err">{{ progress }}</span>
    </div>
    <div v-else-if="progress && !creating" class="co-foot"><span class="co-err">{{ progress }}</span></div>
  </div>
</template>

<style scoped>
.sv-panel { display: flex; flex-direction: column; gap: 12px; min-height: 0; }
.sv-panel.studio .co-foot { position: sticky; bottom: 0; background: #fff; padding: 10px 0; z-index: 2; }
.co-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.co-title { font-size: 16px; font-weight: 800; color: #1a2336; }
.co-subtitle { font-size: 12px; color: #6b7a94; margin-top: 2px; }
.co-actions { display: flex; gap: 4px; flex-shrink: 0; }
.co-title-row { max-width: 520px; }
.co-doc { flex: 1; min-height: 0; }
.co-foot { display: flex; align-items: center; gap: 12px; }
.co-err { font-size: 12px; color: #c0392b; }
</style>
